/**
 * OneVeracity Design Tokens
 *
 * Official brand colors from OneVeracity Brand Guidelines
 * Custom theme tokens that extend Fluent UI's design system
 * with Veracity brand colors while maintaining accessibility.
 */

import {
  createLightTheme,
  createDarkTheme,
  BrandVariants,
  Theme,
} from '@fluentui/react-components';

// =============================================================================
// OneVeracity Official Brand Colors
// =============================================================================

/**
 * OneVeracity Official Color Palette
 * Source: OneVeracity Brand Guidelines
 */
export const veracityColors = {
  // Primary
  veracityOrange: '#D6461D',

  // Secondary Colors
  daybreakAmber: '#F79E31',
  morningCoral: '#EE6352',
  horizonBlue: '#A4C0C1',
  twilightPlum: '#553746',
  duskBlue: '#3F4C5C',
  clarityGreen: '#597B4B',

  // Neutral Colors
  midnightCharcoal: '#1C1919',
  gentleHaze: '#FFE6C8',
  firstLight: '#FFF1E0',
} as const;

/**
 * Brand variants generated from Veracity Orange #D6461D
 * These follow Fluent UI's 16-shade scale from light to dark
 */
const veracityBrand: BrandVariants = {
  10: '#FFF5F2',  // Lightest - subtle backgrounds
  20: '#FFEAE4',  // Light backgrounds
  30: '#FFD4C9',  // Hover backgrounds
  40: '#FFBFAE',
  50: '#FFA993',
  60: '#FF9378',
  70: '#F57D5D',
  80: '#E66742',  // Hover state for primary
  90: '#D6461D',  // Primary brand color (Veracity Orange)
  100: '#C23D19', // Active/pressed state
  110: '#AE3515',
  120: '#9A2D12',
  130: '#86250F',
  140: '#721D0C',
  150: '#5E1509',
  160: '#4A0D06', // Darkest - text on light backgrounds
};

// =============================================================================
// Theme Configuration
// =============================================================================

/**
 * Light theme with Veracity branding
 */
export const veracityLightTheme: Theme = {
  ...createLightTheme(veracityBrand),
};

/**
 * Dark theme with Veracity branding
 */
export const veracityDarkTheme: Theme = {
  ...createDarkTheme(veracityBrand),
};

// =============================================================================
// Semantic Color Tokens
// =============================================================================

/**
 * Semantic colors mapped to OneVeracity brand palette
 */
export const semanticColors = {
  // Status colors (OneVeracity secondary palette)
  success: veracityColors.clarityGreen,     // #597B4B - Clarity Green
  warning: veracityColors.daybreakAmber,    // #F79E31 - Daybreak Amber
  error: veracityColors.morningCoral,       // #EE6352 - Morning Coral
  info: veracityColors.duskBlue,            // #3F4C5C - Dusk Blue

  // Brand accents
  accent: veracityColors.veracityOrange,    // #D6461D - Primary brand
  accentSecondary: veracityColors.horizonBlue, // #A4C0C1 - Horizon Blue
  accentTertiary: veracityColors.twilightPlum, // #553746 - Twilight Plum

  // Light theme surfaces (warm neutrals per UX guide)
  surfaceLight: '#FFFFFF',
  surfaceMuted: veracityColors.firstLight,   // #FFF1E0 - page background
  surfaceWarm: veracityColors.gentleHaze,    // #FFE6C8 - hover/selected
  surfaceElevated: '#FFFFFF',
  surfaceDark: veracityColors.midnightCharcoal, // #1C1919

  // Text colors (warm-tinted per UX guide)
  textPrimary: veracityColors.midnightCharcoal, // #1C1919
  textSecondary: '#6b5240',                  // Warm medium
  textDisabled: '#a8896b',                   // Warm muted
  textOnDark: veracityColors.firstLight,     // #FFF1E0

  // Border colors (warm-tinted per UX guide)
  borderSubtle: '#f0dcc4',
  borderDefault: '#e8d5bc',
  borderStrong: '#c4a98a',
} as const;

// =============================================================================
// Spacing Tokens
// =============================================================================

/**
 * Spacing scale following 4px base unit
 */
export const spacing = {
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
} as const;

// =============================================================================
// Typography Tokens
// =============================================================================

/**
 * Typography settings — DM Sans (body) + JetBrains Mono (numeric)
 * Per UX_GUIDE_UNIFIED.md
 */
export const typography = {
  fontFamily: "'DM Sans', sans-serif",
  fontFamilyMono: "'JetBrains Mono', monospace",

  // Font sizes
  fontSizeBase100: '10px',
  fontSizeBase200: '12px',
  fontSizeBase300: '14px',  // Body text
  fontSizeBase400: '16px',
  fontSizeBase500: '20px',
  fontSizeBase600: '24px',  // Headings

  // Line heights
  lineHeightBase100: '14px',
  lineHeightBase200: '16px',
  lineHeightBase300: '20px',
  lineHeightBase400: '22px',
  lineHeightBase500: '28px',
  lineHeightBase600: '32px',

  // Font weights
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,
} as const;

// =============================================================================
// Shadow Tokens
// =============================================================================

/**
 * Elevation shadows — DEPRECATED per UX guide.
 * Use 1px border instead of shadows on cards and data elements.
 * Kept for Fluent UI compatibility only.
 */
export const shadows = {
  shadow2: 'none',
  shadow4: 'none',
  shadow8: 'none',
  shadow16: 'none',
  shadow28: 'none',
  shadow64: 'none',
} as const;

// =============================================================================
// Border Radius Tokens
// =============================================================================

/**
 * Border radius scale
 */
/**
 * Border radius scale — per UX guide:
 * 8px on data elements (cards, tables), 6px on buttons
 */
export const borderRadius = {
  none: '0px',
  small: '4px',
  medium: '6px',    // Buttons, controls
  large: '8px',     // Cards, data elements
  xLarge: '8px',    // Capped at 8px per UX guide
  circular: '9999px',
} as const;

// =============================================================================
// Breakpoints
// =============================================================================

/**
 * Responsive breakpoints
 */
export const breakpoints = {
  xs: '320px',
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1366px',
  xxl: '1920px',
} as const;

/**
 * Media query helpers
 */
export const mediaQueries = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  xxl: `@media (min-width: ${breakpoints.xxl})`,
} as const;

// =============================================================================
// Animation Tokens
// =============================================================================

/**
 * Animation durations and easings
 */
export const animation = {
  durationUltraFast: '50ms',
  durationFaster: '100ms',
  durationFast: '150ms',
  durationNormal: '200ms',
  durationSlow: '300ms',
  durationSlower: '400ms',
  durationUltraSlow: '500ms',

  easingAccelerate: 'cubic-bezier(0.9, 0.1, 1, 0.2)',
  easingDecelerate: 'cubic-bezier(0.1, 0.9, 0.2, 1)',
  easingLinear: 'cubic-bezier(0, 0, 1, 1)',
  easingMax: 'cubic-bezier(0.8, 0, 0.2, 1)',
} as const;
